import { useEffect, useState } from 'react';
import { api, errorText } from '../api';
import { Dialog } from '../components/Dialog';
import { fmtDate, fmtKB } from '../lib/format';
import { dateiUrl, type KontaktDatei } from './kontakte/KontaktDaten';
import { PdfDialog } from './PdfDialog';
import './stundenzettel-stapel.css';

// Stundenzettel einer Rechnung oder eines Entwurfs (Billtoro): die Zettel, aus denen sie entstanden ist, beim Kunden
// unter „Dateien“ abgelegt (server/src/modules/rechnungstool/stundenzettel-ablage.ts). PDF im Fenster, Fotos in einem
// neuen Tab, jede Datei auch zum Herunterladen. rechnungId: Zettel einer Rechnung (beim Öffnen geladen); dateien: schon
// geladen (Entwurf).
export function StundenzettelFenster({
  open,
  titel,
  rechnungId,
  dateien,
  onClose,
}: {
  open: boolean;
  titel: string;
  rechnungId?: string;
  dateien?: KontaktDatei[];
  onClose(): void;
}) {
  const [geladen, setGeladen] = useState<KontaktDatei[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [pdf, setPdf] = useState<KontaktDatei | null>(null);

  useEffect(() => {
    if (!open || !rechnungId) return;
    let live = true;
    setGeladen(null);
    setFehler(null);
    api
      .get<{ stundenzettel: KontaktDatei[] }>(`/api/rechnungstool/rechnung/${rechnungId}/stundenzettel`)
      .then((r) => live && setGeladen(r.stundenzettel))
      .catch((err) => live && setFehler(errorText(err)));
    return () => {
      live = false;
    };
  }, [open, rechnungId]);

  const list = dateien ?? geladen;
  const ordner = [...new Set((list ?? []).map((d) => d.stundenzettel?.ordner).filter((o): o is string => !!o))];
  return (
    <>
      <Dialog open={open && !pdf} onClose={onClose} title={titel} className="sz-ablage">
        {fehler ? (
          <div className="notice bad">{fehler}</div>
        ) : !list ? (
          <p className="muted">Wird geladen …</p>
        ) : list.length === 0 ? (
          <p className="muted">Keine Stundenzettel.</p>
        ) : (
          <>
            <ul className="sz-ablage-liste">
              {list.map((d) => (
                <li key={d.id}>
                  {d.mime === 'application/pdf' ? (
                    <button type="button" className="linkbtn sz-ablage-name" onClick={() => setPdf(d)}>
                      {d.name}
                    </button>
                  ) : (
                    <a className="sz-ablage-name" href={`${dateiUrl(d.id)}?inline=1`} target="_blank" rel="noopener">
                      {d.name}
                    </a>
                  )}
                  <span className="muted sz-ablage-meta">
                    {fmtKB(d.groesse)} · {fmtDate(d.am)}
                  </span>
                  <a className="linkbtn sz-ablage-laden" href={dateiUrl(d.id)} download aria-label={`${d.name} herunterladen`}>
                    Herunterladen
                  </a>
                </li>
              ))}
            </ul>
            <p className="rt-hint sz-ablage-wo">
              {list.every((d) => d.stundenzettel?.wartet)
                ? `Sobald die Rechnung geschrieben ist, ${list.length === 1 ? 'liegt der Stundenzettel' : 'liegen die Stundenzettel'} beim Kunden der Rechnung unter Dateien, im Ordner ihres Objekts.`
                : `Beim Kunden unter Dateien${ordner.length === 1 ? `, im Ordner „${ordner[0]}“.` : ', je Objekt ein Ordner.'}`}
            </p>
          </>
        )}
        <div className="dlg-actions">
          <button type="button" className="btn primary" onClick={onClose}>
            Schließen
          </button>
        </div>
      </Dialog>
      <PdfDialog
        url={pdf ? `${dateiUrl(pdf.id)}?inline=1` : null}
        title={pdf?.name ?? ''}
        download={pdf ? dateiUrl(pdf.id) : undefined}
        onClose={() => setPdf(null)}
      />
    </>
  );
}
